import { http } from '@/lib/apiClient';
import type {
  Cliente,
  ClienteFilters,
  ClienteFonte,
  ClienteFonteRevisione,
  ClienteFonteVisibilita,
  CreateClienteInput,
  CreateClienteFonteInput,
  FonteAssegnazione,
  Paginated,
  UUID,
  UpdateClienteInput,
  UpdateClienteFonteInput,
  VisibilitaCliente,
  VisibilitaWenkey,
} from '@/types';

const BASE = '/clienti';

export interface ClienteFinaleLink {
  id: UUID;
  partner_id: UUID;
  cliente_finale_id: UUID;
  attivo_dal: string;
  note: string | null;
  cliente_finale: {
    id: UUID;
    ragione_sociale: string;
    partita_iva?: string | null;
    codice_sdi?: string | null;
    email_principale: string | null;
    telefono: string | null;
    indirizzo?: string | null;
    stato: string;
    tipo: 'partner' | 'cliente_finale';
  } | null;
}

/**
 * Tenant CRUD. Reachable only by `operatore`/`admin`: RLS returns an empty set
 * for tenant users, and the route guard hides the pages anyway.
 */
export const clientiService = {
  list(filters: ClienteFilters = {}): Promise<Paginated<Cliente>> {
    return http.get<Paginated<Cliente>>(BASE, { params: filters });
  },

  get(id: UUID): Promise<Cliente> {
    return http.get<Cliente>(`${BASE}/${id}`);
  },

  listClientiFinali(partnerId: UUID): Promise<ClienteFinaleLink[]> {
    return http.get<ClienteFinaleLink[]>(`${BASE}/${partnerId}/clienti-finali`);
  },

  createClienteFinale(partnerId: UUID, input: CreateClienteInput): Promise<Cliente> {
    return http.post<Cliente, CreateClienteInput>(`${BASE}/${partnerId}/clienti-finali`, input);
  },

  updateClienteFinale(partnerId: UUID, finaleId: UUID, input: UpdateClienteInput): Promise<Cliente> {
    return http.patch<Cliente, UpdateClienteInput>(`${BASE}/${partnerId}/clienti-finali/${finaleId}`, input);
  },

  removeClienteFinale(partnerId: UUID, finaleId: UUID): Promise<void> {
    return http.delete(`${BASE}/${partnerId}/clienti-finali/${finaleId}`);
  },

  create(input: CreateClienteInput): Promise<Cliente> {
    return http.post<Cliente, CreateClienteInput>(BASE, input);
  },

  update(id: UUID, input: UpdateClienteInput): Promise<Cliente> {
    return http.patch<Cliente, UpdateClienteInput>(`${BASE}/${id}`, input);
  },

  /**
   * Suspension is a state transition rather than a generic update so the
   * backend can cascade (disable users, pause ingestion) and write an audit row.
   * The NestJS endpoint takes no body (`POST /clienti/:id/suspend`).
   */
  suspend(id: UUID): Promise<Cliente> {
    return http.post<Cliente>(`${BASE}/${id}/suspend`);
  },

  reactivate(id: UUID): Promise<Cliente> {
    return http.post<Cliente>(`${BASE}/${id}/reactivate`);
  },

  /** Superadmin: activate a self-registered company (`in_attivazione` → `attivo`). */
  attiva(id: UUID): Promise<Cliente> {
    return http.post<Cliente>(`${BASE}/${id}/attiva`);
  },

  remove(id: UUID): Promise<void> {
    return http.delete(`${BASE}/${id}`);
  },

  listPersi(filters: ClienteFilters = {}): Promise<Paginated<Cliente>> {
    return http.get<Paginated<Cliente>>(`${BASE}/persi`, { params: filters });
  },

  restore(id: UUID): Promise<Cliente> {
    return http.post<Cliente>(`${BASE}/${id}/ripristina`);
  },

  purge(id: UUID): Promise<void> {
    return http.delete(`${BASE}/${id}/definitivo`);
  },

  listFonti(id: UUID): Promise<ClienteFonte[]> {
    return http.get<ClienteFonte[]>(`${BASE}/${id}/fonti`);
  },

  listFontiVisibilita(filters: {
    page?: number;
    pageSize?: number;
    search?: string;
    clienteId?: UUID;
  }): Promise<Paginated<ClienteFonteVisibilita>> {
    return http.get<Paginated<ClienteFonteVisibilita>>(`${BASE}/fonti/visibilita`, { params: filters });
  },

  setFonteVisibilitaWenkey(id: UUID, fonteId: UUID, visibilita: VisibilitaWenkey): Promise<ClienteFonte> {
    return http.patch<ClienteFonte, { visibilita: VisibilitaWenkey }>(
      `${BASE}/${id}/fonti/${fonteId}/visibilita-wenkey`,
      { visibilita },
    );
  },

  setFonteVisibilitaCliente(id: UUID, fonteId: UUID, visibilita: VisibilitaCliente): Promise<ClienteFonte> {
    return http.patch<ClienteFonte, { visibilita: VisibilitaCliente }>(
      `${BASE}/${id}/fonti/${fonteId}/visibilita-cliente`,
      { visibilita },
    );
  },

  listFonteAssegnazioni(id: UUID, fonteId: UUID): Promise<FonteAssegnazione[]> {
    return http.get<FonteAssegnazione[]>(`${BASE}/${id}/fonti/${fonteId}/assegnazioni`);
  },

  assegnaFonte(id: UUID, fonteId: UUID, utente_ids: UUID[], puo_scaricare = true): Promise<FonteAssegnazione[]> {
    return http.post<FonteAssegnazione[], { utente_ids: UUID[]; puo_scaricare: boolean }>(
      `${BASE}/${id}/fonti/${fonteId}/assegna`,
      { utente_ids, puo_scaricare },
    );
  },

  revocaFonteAssegnazione(id: UUID, fonteId: UUID, utenteId: UUID): Promise<void> {
    return http.delete(`${BASE}/${id}/fonti/${fonteId}/assegna/${utenteId}`);
  },

  createFonte(id: UUID, input: CreateClienteFonteInput): Promise<ClienteFonte> {
    return http.post<ClienteFonte, CreateClienteFonteInput>(`${BASE}/${id}/fonti`, input);
  },

  updateFonte(id: UUID, fonteId: UUID, input: UpdateClienteFonteInput): Promise<ClienteFonte> {
    return http.patch<ClienteFonte, UpdateClienteFonteInput>(`${BASE}/${id}/fonti/${fonteId}`, input);
  },

  uploadFonte(id: UUID, file: File, meta: { titolo?: string; descrizione?: string } = {}): Promise<ClienteFonte> {
    const form = new FormData();
    form.append('file', file);
    if (meta.titolo) form.append('titolo', meta.titolo);
    if (meta.descrizione) form.append('descrizione', meta.descrizione);
    return http.post<ClienteFonte, FormData>(`${BASE}/${id}/fonti/upload`, form);
  },

  downloadFonte(id: UUID, fonteId: UUID): Promise<Blob> {
    return http.get<Blob>(`${BASE}/${id}/fonti/${fonteId}/download`, { responseType: 'blob' });
  },

  removeFonte(id: UUID, fonteId: UUID): Promise<void> {
    return http.delete(`${BASE}/${id}/fonti/${fonteId}`);
  },

  listCestino(id: UUID): Promise<ClienteFonte[]> {
    return http.get<ClienteFonte[]>(`${BASE}/${id}/fonti/cestino`);
  },

  restoreFonte(id: UUID, fonteId: UUID): Promise<ClienteFonte> {
    return http.post<ClienteFonte>(`${BASE}/${id}/fonti/${fonteId}/ripristina`);
  },

  purgeFonte(id: UUID, fonteId: UUID): Promise<void> {
    return http.delete(`${BASE}/${id}/fonti/${fonteId}/definitivo`);
  },

  listRevisioni(id: UUID, fonteId: UUID): Promise<ClienteFonteRevisione[]> {
    return http.get<ClienteFonteRevisione[]>(`${BASE}/${id}/fonti/${fonteId}/revisioni`);
  },

  restoreRevisione(id: UUID, fonteId: UUID, revId: UUID): Promise<ClienteFonte> {
    return http.post<ClienteFonte>(`${BASE}/${id}/fonti/${fonteId}/revisioni/${revId}/ripristina`);
  },
};
